import React, { useEffect, type JSX } from 'react';
import { Stack, Button, Box, CircularProgress } from '@mui/material';
import Add from '@mui/icons-material/Add';
import Workspaces from '@mui/icons-material/Workspaces';
import { EmptyState } from '@brightlayer-ui/react-components';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { addGroupModal } from '@fiji/common/src/features/group/groupSlice';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { Hierarchy, renderGroupHierarchyIcons } from '../../components';
import { GroupHierarchySkeleton } from '../GroupHierarchy/GroupHierarchySkeleton';
import { useGetAllGroupsMutation } from '@fiji/common/src/features/group/groupApi';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

export const LoadHierarchy = React.memo((props: any): JSX.Element => {
    const theme: any = useTheme();
    const currentOrg = useTypedSelector(selectedOrg);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [groupsData, setGroupsData] = React.useState<any>();
    const [hierarchyMappingData, setHierarchyMappingData] = React.useState<any>({});
    const [loadingHierarchyNode, setLoadingHierarchyNode] = React.useState([]);
    const [getAllGroups, { data: groupsHierarchy, isLoading: isGroupsLoading, isUninitialized }]: any =
        useGetAllGroupsMutation();

    useEffect(() => {
        if (groupsHierarchy?.data?.records !== undefined) {
            setGroupsData(groupsHierarchy?.data?.records ?? []);
        }
    }, [groupsHierarchy]);

    React.useEffect(() => {
        if (currentOrg?.id) {
            getAllGroups({});
        }
    }, [currentOrg]);

    const handleFetchHierarchy = async (parentData: any): Promise<void> => {
        setLoadingHierarchyNode((prevState): any => [...prevState, parentData?.id]);

        const { data: childHierarchyData }: any = await getAllGroups({ parent: parentData?.id });
        if (childHierarchyData) {
            setHierarchyMappingData((prevState: any) => ({
                ...prevState,
                [parentData?.id]: childHierarchyData?.data?.records,
            }));
        }

        setLoadingHierarchyNode((prevState): any => prevState.filter((loadingNode) => loadingNode !== parentData?.id));
    };
    const groupSelectionHandler = (selectedNodeData: any): void => {
        props?.setSelectDeviceId?.(selectedNodeData);
    };

    const handleOpenAddGroupModal = (groupId?: string): any =>
        dispatch(addGroupModal({ isOpen: true, group: groupId ?? '' }));

    const shouldShowLoader = groupsData === undefined && (isGroupsLoading || isUninitialized);
    const hierarchyData = currentOrg?.defaultGroupAccess
        ? [
              {
                  ...currentOrg,
                  name: currentOrg?.name,
                  groupCount: currentOrg?.groupCount,
                  deviceCount: currentOrg?.deviceCount,
                  type: 'GROUP',
                  icon: { web: { family: 'material-ui', name: 'CorporateFare' } },
              },
          ]
        : groupsData;

    const getGroupList = (): JSX.Element =>
        shouldShowLoader ? (
            <Box
                sx={{ height: 'calc(100vh - 336px)' }}
                width={'100%'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
            >
                <CircularProgress sx={{ color: '#007bc1' }} size={60} />
            </Box>
        ) : hierarchyData?.length ? (
            <Hierarchy
                isLoading={shouldShowLoader}
                data={hierarchyData}
                hasChild={(treeItem: any): boolean => treeItem?.groupCount || treeItem?.deviceCount}
                labelKey={'name'}
                hierarchyMappingData={hierarchyMappingData}
                loadingNode={loadingHierarchyNode}
                handleTreeNodeSelection={groupSelectionHandler}
                getAvatarChild={renderGroupHierarchyIcons}
                autoChildSelection={false}
                loadMore={handleFetchHierarchy}
                loadingSkeleton={<GroupHierarchySkeleton row={1} />}
                selectedNodes={props?.selectedDeviceId ?? ''}
                selectionType={'radio'} // radio, checkbox, node
                filters={[
                    {
                        key: 'type',
                        operator: '!==',
                        value: 'GATEWAY',
                        action: 'disableCheck',
                    },
                    {
                        key: 'type',
                        operator: '!==',
                        value: 'GROUP',
                        action: 'disableCheck',
                    },
                ]}
            />
        ) : (
            <EmptyState
                icon={<Workspaces fontSize={'inherit'} className="height-76 width-76 color-icon" />}
                title={t('GROUP_MANAGEMENT.LABELS.NO_GROUPS')}
                actions={
                    <Stack direction={'column'} spacing={2}>
                        <Button
                            sx={{
                                border: `1px solid ${theme?.palette?.primary?.main}`,
                                '&:hover': {
                                    backgroundColor: theme?.palette?.primary?.[50],
                                    color: theme?.palette?.primary?.main,
                                },
                            }}
                            variant={'outlined'}
                            color={'primary'}
                            startIcon={<Add />}
                            onClick={(): void => handleOpenAddGroupModal()}
                        >
                            {' '}
                            {t('GROUP_MANAGEMENT.BTN.ADD_A_GROUP')}{' '}
                        </Button>
                    </Stack>
                }
                description={t('COMMON.SELECT_GROUP_NO_DESC')}
            />
        );

    return <Box>{getGroupList()}</Box>;
});
